/**
 * Watermark overlay for screenshot protection.
 * Only shows on sensitive data pages (customers, invoices, contracts, employees, services, reports).
 * Text follows wavy SVG paths for an organic, hard-to-remove look.
 */
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import useAuthStore from '@/stores/useAuthStore'

/** Route prefixes that contain sensitive data. Empty = show on all pages. */
const SENSITIVE_PATHS: string[] = []

/** Wavy path definitions — each has unique curve shape */
const PATHS = [
    { id: 'p1', d: 'M0,80 Q250,20 500,90 T1000,70 T1500,100 T2000,60', y: 100 },
    { id: 'p2', d: 'M0,60 Q300,120 600,50 T1200,80 T1800,40 T2000,90', y: 300 },
    {
        id: 'p3',
        d: 'M0,100 Q200,40 450,110 T900,60 T1350,100 T2000,50',
        y: 500,
    },
    { id: 'p4', d: 'M0,50 Q350,110 700,40 T1100,90 T1600,50 T2000,80', y: 700 },
    { id: 'p5', d: 'M0,90 Q280,30 550,100 T1050,50 T1500,90 T2000,70', y: 900 },
    {
        id: 'p6',
        d: 'M0,70 Q320,130 640,60 T1280,100 T1700,40 T2000,85',
        y: 1100,
    },
]

const Watermark = () => {
    const { employee } = useAuthStore()
    const { pathname } = useLocation()

    // Show on all pages when SENSITIVE_PATHS is empty, otherwise filter by path
    const isSensitive =
        SENSITIVE_PATHS.length === 0 ||
        SENSITIVE_PATHS.some((p) => pathname.startsWith(p))

    const svg = useMemo(() => {
        if (!isSensitive) return null

        const name = employee?.employee_name || ''
        const email = employee?.employee_email_company || ''
        if (!name && !email) return null

        const info = `${name}  •  ${email}`
            .replace(/&/g, '&amp;')
            .replace(/'/g, '&apos;')
            .replace(/</g, '&lt;')

        const repeatedTitle = Array(5).fill('CONFIDENTIAL    ').join('')
        const repeatedInfo = Array(4).fill(`${info}    `).join('')

        const defs = PATHS.map(
            (p) => `<path id='${p.id}' d='${p.d}' fill='none'/>`
        ).join('')

        const texts = PATHS.map((p, i) => {
            const isTitle = i % 2 === 0
            const content = isTitle ? repeatedTitle : repeatedInfo
            const fontSize = isTitle ? 40 : 14
            const weight = isTitle ? "font-weight='bold'" : ''
            return `<text transform='translate(0,${p.y})' font-family='Arial,sans-serif' font-size='${fontSize}' ${weight} fill='%23000'>
                <textPath href='%23${p.id}'>${content}</textPath>
            </text>`
        }).join('')

        return `<svg xmlns='http://www.w3.org/2000/svg' width='2000' height='1200'>
            <defs>${defs}</defs>
            ${texts}
        </svg>`
    }, [employee, isSensitive])

    if (!svg) return null

    const backgroundImage = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`

    return (
        <div
            className="fixed inset-0 z-[9999] pointer-events-none select-none overflow-hidden"
            aria-hidden="true"
        >
            <div
                className="w-full h-full opacity-[0.05]"
                style={{ backgroundImage, backgroundRepeat: 'repeat' }}
            />
        </div>
    )
}

export default Watermark
